import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { useAppDispatch } from "../../../app/store";

import SidePeek from "../../../components/ui/sidepeek/SidePeek";
import { CustomLoadingSpinner } from "../../../components/ui/spinners/LoadingSpinner";
import { ErrorDisplay } from "../../../components/ui/error/ErrorDisplay";

import { fetchEnrichedStakeholder } from "../logic/stakeholderThunks";

import LinkedLoanAccounts from "./LinkedLoanAccounts";
import BorrowerDetails from "./BorrowerDetails";
import StakeholderHeader from "./StakeHolderHeader";
import StakeholderKyc from "./StakeholderKyc";
import StakeholderContact from "./StakeholderContact";

interface BorrowerSidePeekProps {
  stakeholderId: string;
  onClose: () => void;
  onAccountClick: (accountNumber: string) => void;
}

const Divider = () => (
  <div className="h-px w-full shrink-0 bg-[#E5E5E5]" />
);

const BorrowerSidePeek: React.FC<BorrowerSidePeekProps> = ({
  stakeholderId,
  onClose,
  onAccountClick,
}) => {
  const { id: caseId } = useParams<{ id: string }>();

  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const loadStakeholder = async () => {
    if (!caseId || !stakeholderId) {
      return;
    }

    setIsLoading(true);
    setHasError(false);

    const result = await dispatch(
      fetchEnrichedStakeholder({
        caseId: caseId,
        stakeholderIds: [stakeholderId],
      }),
    );

    if (fetchEnrichedStakeholder.fulfilled.match(result)) {
      setIsLoading(false);
      return;
    }

    setHasError(true);
    setIsLoading(false);
  };

  useEffect(() => {
    loadStakeholder();
  }, [caseId, stakeholderId]);

  const handleAccountClick = (selectedAccountNumber: string) => {
    onAccountClick(selectedAccountNumber);
    onClose();
  };

  return (
    <SidePeek
      title="Stakeholder Details"
      onClose={onClose}
    >
      {isLoading ? (
        <div className="flex w-full p-4">
          <CustomLoadingSpinner
            title="Loading stakeholder details"
            subtitle="Fetching stakeholder information..."
            minHeight="min-h-[350px]"
            className="w-full"
          />
        </div>
      ) : hasError ? (
        <div className="p-4">
          <ErrorDisplay
            error="Failed to load stakeholder details. Please retry."
            onRetry={loadStakeholder}
          />
        </div>
      ) : (
        <div className="flex w-full flex-col gap-6 mt-4 mb-6">
          <StakeholderHeader
            stakeholderId={stakeholderId}
          />

          <LinkedLoanAccounts
            stakeholderId={stakeholderId}
            onClick={handleAccountClick}
          />

          <Divider />

          <BorrowerDetails stakeholderId={stakeholderId}/>

          <Divider />

          <StakeholderKyc stakeholderId={stakeholderId} />

           <Divider />

          <StakeholderContact stakeholderId={stakeholderId} />

        </div>
      )}
    </SidePeek>
  );
};

export default BorrowerSidePeek;